import { useTheme } from '@emotion/react';
import { Fragment, memo, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useMedia } from 'react-use';

import type {
  ACHIEVEMENT_TIERS,
  ACHIEVEMENT_TYPES,
} from '@didaskill/domain/user-achievements/constants';
import dayjs from '@didaskill/services/dayjs';
import { Tag, Tile, type TileProps } from '@didaskill/ui/design-system';
import { achievementMessages } from '@didaskill/ui/intl';
import { useBoolState } from '@didaskill/ui/utils';

import { AchievementTierTag } from '../achievement-tier-tag/AchievementTierTag';
import messages from './AchievementTile.messages';
import { AchievementTileDialog } from './achievement-tile-dialog/AchievementTileDialog';
import { AchievementTileIcon } from './achievement-tile-icon/AchievementTileIcon';
import { ACHIEVEMENT_TILE_VARIANTS_MAP } from './constants';

// Discriminated union to make unlockedAt required when isLocked is true

type UnlockedAchievementProps = {
  isLocked?: false;
  unlockedAt?: never;
};

type LockedAchievementProps = {
  isLocked: true;
  unlockedAt: Date;
};

export type AchievementTileProps = ({
  type: ACHIEVEMENT_TYPES;
  tier: ACHIEVEMENT_TIERS;
  isOwned?: boolean;
} & (UnlockedAchievementProps | LockedAchievementProps)) &
  Omit<TileProps, 'icon' | 'title' | 'subtitle'>;

const AchievementTileBase = ({
  type,
  tier,
  isOwned,
  isLocked = false,
  unlockedAt,
  ...props
}: AchievementTileProps) => {
  const intl = useIntl();
  const [isDialogOpen, onOpenDialog, onCloseDialog] = useBoolState();
  const { breakpoints } = useTheme();

  const isAboveMedium = useMedia(`(min-width: ${breakpoints.medium})`);

  const correspondingMessages = useMemo(() => achievementMessages[type][tier], [type, tier]);

  const title = useMemo(
    () =>
      intl.formatMessage(
        correspondingMessages ? correspondingMessages.TITLE : messages.TITLE_PLACEHOLDER,
      ),

    [intl, correspondingMessages],
  );

  const description = useMemo(
    () =>
      intl.formatMessage(
        correspondingMessages
          ? correspondingMessages.DESCRIPTION
          : messages.DESCRIPTION_PLACEHOLDER,
      ),
    [intl, correspondingMessages],
  );

  const formattedUnlockedAt = useMemo(() => {
    if (!unlockedAt) return null;
    return dayjs(unlockedAt).format('DD/MM/YY');
  }, [unlockedAt]);

  const elementRight = useMemo(() => {
    if (isOwned) {
      return (
        <AchievementTierTag tier={tier} size={isAboveMedium ? Tag.SIZES.MEDIUM : Tag.SIZES.SMALL} />
      );
    }

    if (isLocked && unlockedAt) {
      return (
        <Tag
          label={formattedUnlockedAt}
          variant={Tag.VARIANTS.NEUTRAL}
          size={isAboveMedium ? Tag.SIZES.MEDIUM : Tag.SIZES.SMALL}
        />
      );
    }

    return null;
  }, [isOwned, isLocked, tier, unlockedAt, formattedUnlockedAt, isAboveMedium]);

  return (
    <Fragment>
      <Tile
        onClick={onOpenDialog}
        icon={
          <AchievementTileIcon isDisabled={!isOwned} isLocked={isLocked} tier={tier} type={type} />
        }
        variant={isOwned && !isLocked ? ACHIEVEMENT_TILE_VARIANTS_MAP[tier] : Tile.VARIANTS.NEUTRAL}
        isDisabled={!isOwned}
        shouldOverrideIconStyle={true}
        title={title}
        elementRight={elementRight}
        subtitle={description}
        {...props}
      />

      <AchievementTileDialog
        isOpen={isDialogOpen}
        onRequestClose={onCloseDialog}
        title={title}
        achievementType={type}
        isOwned={isOwned}
        description={description}
        achievementTier={tier}
      />
    </Fragment>
  );
};

AchievementTileBase.displayName = 'AchievementTile';

export const AchievementTile = memo(AchievementTileBase);
